import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { SafeDictionary } from "@insite/client-framework/Common/Types";
import { HasProductContext, withProductContext } from "@insite/client-framework/Components/ProductContext";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import { getUnitNetPrice } from "@insite/client-framework/Services/Helpers/ProductPriceService";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getCategoriesDataView } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategories from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategories";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { canAddToCart, hasEnoughInventory } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import updateCartLine from "@insite/client-framework/Store/Pages/Cart/Handlers/UpdateCartLine";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductSubscriptionDto } from "@insite/client-framework/Types/ApiModels";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import BrandAccessDeniedModal from "@tSurya/Components/BrandAccessDeniedModal";
import RollPadModal from "@tSurya/Components/RollPadModal";
import RugPadInfoDisplay from "@tSurya/Components/RugPadModal";
import { tryParseSelections } from "@tSurya/Common/MgbwConfiguratorHelpers";
import { MGBW_SELECTIONS, MGBW_SELECTIONS_INPUT } from "@tSurya/Common/MgbwPropertyKeys";
import { buildConfiguratorLineInput } from "@tSurya/Common/MgbwConfiguratorHelpers";
import { isGlobalViewsWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import Constant from "@tSurya/Constants/Constant";
import addToCart from "@tSurya/Handlers/AddToCartHandler/AddToCartHandler";
import CustomCartLoadProducts from "@tSurya/Handlers/CustomCartLoadProducts";
import CustomApplicationState from "@tSurya/Store/Data/CustomApplicationState/CustomApplicationState";
import React, { useContext, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    labelOverride?: string;
    extendedStyles?: ButtonPresentationProps;
    notes?: string;
    productType?: string;
    configurationSelection?: SafeDictionary<string>;
    configurationCompleted?: boolean;
    variantSelectionCompleted?: boolean;
    subscription?: ProductSubscriptionDto;
    buttonDisable?: boolean;
    onAddToCartClick?: () => void;
    outdoorSafe: boolean;
    productName?: string;
    showRugPadPopup?: boolean;
    useLocalLoadingState?: boolean;
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasProductContext;

const mapStateToProps = (state: CustomApplicationState, props: OwnProps & HasProductContext) => {
    const {
        productSettings: { showAddToCartConfirmationDialog },
        cartSettings: { addToCartPopupTimeout },
    } = getSettingsCollection(state);
    const location = getLocation(state);
    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;
    const cart = getCurrentCartState(state).value;

    return {
        canAddToCart: canAddToCart(
            state,
            props.productContext.product,
            props.productContext.productInfo,
            props.configurationCompleted,
            props.variantSelectionCompleted,
        ),
        hasEnoughInventory: hasEnoughInventory(state, props.productContext),
        isAddToCartButtonDisabled:
            !(props.useLocalLoadingState && state.SURData.projectLineAddToCart.activeRequestCount > 0) &&
            (isCartLoading || isUpdatingCart),
        showAddToCartConfirmationDialog,
        addToCartPopupTimeout,
        categoriesData: getCategoriesDataView(state, {}),
        itemValue: props.productContext.product.childTraitValues,
        isAuthenticated: state.context.session.isAuthenticated,
        session: getSession(state),
        location,
        cart,
    };
};

const mapDispatchToProps = {
    addToCart: makeHandlerChainAwaitable<Parameters<typeof addToCart>[0], CartLineModel>(addToCart),
    CustomCartLoadProducts,
    loadCategories,
    updateCartLine,
    beginProjectLineAddToCart: () => ({ type: "Pages/MyListsDetails/BeginProjectLineAddToCart" }),
    completeProjectLineAddToCart: () => ({ type: "Pages/MyListsDetails/CompleteProjectLineAddToCart" }),
};

export const productAddToCartButtonStyles: ButtonPresentationProps = {};

const ProductAddToCartButton = ({
    productContext: {
        product: { id: productId, allowZeroPricing, quoteRequired, productNumber, variantTraits, properties },
        productInfo: { qtyOrdered, unitOfMeasure, pricing, failedToLoadInventory, failedToLoadPricing },
    },
    hasEnoughInventory,
    isAddToCartButtonDisabled,
    showAddToCartConfirmationDialog,
    addToCartPopupTimeout,
    addToCart,
    canAddToCart,
    labelOverride,
    extendedStyles,
    notes,
    productType,
    configurationSelection,
    configurationCompleted,
    variantSelectionCompleted,
    subscription,
    onAddToCartClick,
    buttonDisable,
    categoriesData,
    CustomCartLoadProducts,
    loadCategories,
    itemValue,
    isAuthenticated,
    session,
    outdoorSafe,
    productName,
    location,
    updateCartLine,
    cart,
    showRugPadPopup,
    useLocalLoadingState,
    beginProjectLineAddToCart,
    completeProjectLineAddToCart,
    ...otherProps
}: Props) => {
    const toasterContext = useContext(ToasterContext);
    const [styles] = useState(() => mergeToNew(productAddToCartButtonStyles, extendedStyles));
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isRollPadModelOpen, setIsRollPadModelOpen] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [linkedToItem, setLinkedToItem] = useState("");
    const [showBrandDeniedModal, setShowBrandDeniedModal] = useState(false);
    const [isAddingToCart, setIsAddingToCart] = useState(false);

    const isSample =
        variantTraits?.[0]?.traitValues?.find(x => x.value === productName)?.valueDisplay.includes("SAMPLE") ?? false;
    useEffect(() => {
        const shouldOpenModal = localStorage.getItem("showPopup");
        if (productType === Constant.productType) {
            setShowPopup(
                showRugPadPopup !== false && !isSample && !isGlobalViewsWebsite() && shouldOpenModal !== "false",
            );
        }
    }, [productType, isSample, showRugPadPopup]);

    const handleCloseModal = () => {
        setIsModalOpen(false);
        const shouldOpenModal = localStorage.getItem("showPopup");
        setShowPopup(shouldOpenModal !== "false");
    };

    const handleRollPadService = () => {
        setIsRollPadModelOpen(false);
        addToCartClickHandler(true);
    };

    const handleCloseRollPadModal = () => {
        setIsRollPadModelOpen(false);
        addToCartClickHandler(false);
    };

    const addToCartButtonHandler = () => {
        const queryParams = new URLSearchParams(window.location.search);
        const linkedItem = queryParams.get("itemName");
        if (productType === Constant.customProductType && linkedItem && !isGlobalViewsWebsite()) {
            if (linkedItem !== linkedToItem) {
                setLinkedToItem(linkedItem);
                setIsRollPadModelOpen(true);
            } else {
                addToCartClickHandler(false);
            }
        }
        else {
            addToCartClickHandler(false);
        }
    };

    const addToCartClickHandler = async (isRollPadService: boolean) => {
        onAddToCartClick?.();

        const effectiveIsRollPadService = isRollPadService && !isGlobalViewsWebsite();

        if (!quoteRequired && !allowZeroPricing && pricing && getUnitNetPrice(pricing, 1).price === 0) {
            toasterContext.addToast({ body: siteMessage("Cart_InvalidPrice"), messageType: "danger" });
            return;
        }

        const sectionOptions =
            configurationSelection && Object.values(configurationSelection).length > 0
                ? Object.values(configurationSelection)
                      .filter(sectionOptionId => !!sectionOptionId)
                      .map(sectionOptionId => ({ sectionOptionId }))
                : undefined;

        const configuratorInput = buildConfiguratorLineInput(properties);
        const additionalProperties: Record<string, string> | undefined =
            Object.keys(configuratorInput).length > 0 ? configuratorInput : undefined;

        let cartline: CartLineModel | undefined;
        if (useLocalLoadingState) {
            setIsAddingToCart(true);
            beginProjectLineAddToCart();
        }

        try {
            cartline = (await addToCart({
                productId,
                qtyOrdered,
                unitOfMeasure,
                notes,
                sectionOptions,
                subscription,
                linkedToItem,
                isRollPadService: effectiveIsRollPadService,
                additionalProperties,
                onError: (error: string) => {
                    if (error === siteMessage("SignInInfo_BrandAccessDenied")) {
                        setShowBrandDeniedModal(true);
                    } else {
                        toasterContext.addToast({ body: error, messageType: "danger" });
                    }
                },
            })) as CartLineModel | undefined;
        } finally {
            if (useLocalLoadingState) {
                setIsAddingToCart(false);
                completeProjectLineAddToCart();
            }
        }

        if (!cartline) {
            return;
        }

        if (showAddToCartConfirmationDialog) {
            toasterContext.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={cartline.isQtyAdjusted} multipleProducts={false} />,
                messageType: "success",
                timeoutLength: cartline.isQtyAdjusted ? 1000 * 60 * 60 * 24 * 365 : addToCartPopupTimeout,
            });
            setIsRollPadModelOpen(false);
        }

        // Do not open modal on GlobalViews for Rug products
        if (productType === Constant.productType && showPopup && !isGlobalViewsWebsite()) {
            setTimeout(() => {
                setIsModalOpen(true);
            }, 1500);
        }
    };

    return (
        <>
            <Button
                variant="primary"
                onClick={addToCartButtonHandler}
                disabled={
                    qtyOrdered <= 0 ||
                    isAddToCartButtonDisabled ||
                    isAddingToCart ||
                    !!failedToLoadPricing ||
                    !!productNumber.toLowerCase().endsWith(Constant.customProduct)
                }
                {...otherProps}
                {...styles}
            >
                {labelOverride ?? translate("Add to Cart")}
            </Button>
            {showPopup && !isGlobalViewsWebsite() && (
                <RugPadInfoDisplay
                    openModalFlag={isModalOpen}
                    onCloseModal={handleCloseModal}
                    outdoorSafe={outdoorSafe}
                    productName={productName}
                />
            )}
            <RollPadModal
                openModalFlag={isRollPadModelOpen}
                onCloseModal={handleCloseRollPadModal}
                setIsRollPadServiceTrue={handleRollPadService}
            />
            <BrandAccessDeniedModal isOpen={showBrandDeniedModal} onClose={() => setShowBrandDeniedModal(false)} />
        </>
    );
};

export default withProductContext(connect(mapStateToProps, mapDispatchToProps)(ProductAddToCartButton));
